import { 
  searchGmailMessages, 
  findRecentUnreadEmails, 
  findEmailsFromSender, 
  findEmailsBySubject, 
  findEmailsByTopic 
} from './gmailService';

// Helper function to check if a message contains Gmail queries
export function detectGmailQuery(message) {
  // Define keywords and patterns for detecting Gmail-related queries
  const gmailKeywords = [
    'gmail', 'email', 'mail', 'inbox', 'message', 'unread',
    'emails from', 'emails about', 'find email', 'search mail',
    'recent emails', 'email inbox', 'check email'
  ];
  
  // Check if message contains any Gmail-related keywords
  const lowercaseMessage = message.toLowerCase();
  return gmailKeywords.some(keyword => lowercaseMessage.includes(keyword));
}

// Check specifically for unread emails query
export function detectUnreadEmailsQuery(message) {
  const unreadKeywords = ['unread', 'new mail', 'new email', 'unread messages'];
  const queryPatterns = [
    'do i have', 'are there', 'show me', 'get', 'fetch', 'find', 
    'check', 'tell me about', 'what are', 'any'
  ];
  
  const lowercaseMessage = message.toLowerCase();
  
  // Check if message contains unread keywords AND a query pattern
  const hasUnreadKeyword = unreadKeywords.some(keyword => 
    lowercaseMessage.includes(keyword)
  );
  
  const hasQueryPattern = queryPatterns.some(pattern => 
    lowercaseMessage.includes(pattern)
  );
  
  return hasUnreadKeyword && hasQueryPattern;
}

// Process Gmail requests based on user message
export async function processGmailRequest(message, companyId) {
  try {
    if (!companyId) {
      return {
        success: false,
        message: "Gmail is not connected. Please connect your Google Gmail in Settings."
      };
    }
    
    const lowercaseMessage = message.toLowerCase();
    
    // Check for unread emails query first
    if (detectUnreadEmailsQuery(message)) {
      return await getUnreadEmails(companyId);
    }
    
    // Check for "emails from" specific sender
    const fromMatch = lowercaseMessage.match(/emails? from (.+?)($|\s|\.)/i);
    if (fromMatch && fromMatch[1]) {
      return await getEmailsFromSender(companyId, fromMatch[1].trim());
    }
    
    // Check for "emails about" or "subject" specific topic
    const subjectMatch = lowercaseMessage.match(/emails? about|with subject|subject:?(.+?)($|\s|\.)/i);
    if (subjectMatch && subjectMatch[1]) {
      return await getEmailsBySubject(companyId, subjectMatch[1].trim());
    }
    
    // Check for specific topic search
    if (lowercaseMessage.includes('find emails') || lowercaseMessage.includes('search emails') || 
        lowercaseMessage.includes('find mail') || lowercaseMessage.includes('search mail')) {
      // Extract the search term - look for patterns like "find emails about X" or "search for emails with X"
      const searchMatches = lowercaseMessage.match(/find|search(?:\s+for)?(?:\s+emails?|\s+mail)?(?:\s+about|\s+with|\s+containing|\s+on|\s+regarding)?(.+?)($|\s|\.)/i);
      if (searchMatches && searchMatches[1]) {
        return await getEmailsByTopic(companyId, searchMatches[1].trim());
      }
    }
    
    // Default to recent emails for generic Gmail requests
    return await getRecentEmails(companyId);
    
  } catch (error) {
    console.error('Error processing Gmail request:', error);
    return {
      success: false,
      message: `Error accessing Gmail: ${error.message}`
    };
  }
}

// Get unread emails
async function getUnreadEmails(companyId, maxResults = 5) {
  try {
    const emails = await findRecentUnreadEmails(companyId, maxResults);
    
    if (!emails || emails.length === 0) {
      return {
        success: true,
        message: "You don't have any unread emails in your inbox.",
        data: null
      };
    }
    
    return {
      success: true,
      message: `You have ${emails.length} unread emails:`,
      data: {
        type: 'unreadEmails',
        emails: emails
      }
    };
  } catch (error) {
    console.error('Error fetching unread emails:', error);
    return {
      success: false,
      message: `Error fetching unread emails: ${error.message}`
    };
  }
}

// Get recent emails (default action)
async function getRecentEmails(companyId, maxResults = 5) {
  try {
    const emails = await searchGmailMessages(companyId, '', maxResults);
    
    if (!emails || emails.length === 0) {
      return {
        success: true,
        message: "You don't have any recent emails in your inbox.",
        data: null
      };
    }
    
    return {
      success: true,
      message: `Here are your ${emails.length} most recent emails:`,
      data: {
        type: 'recentEmails',
        emails: emails
      }
    };
  } catch (error) {
    console.error('Error fetching recent emails:', error);
    return {
      success: false,
      message: `Error fetching recent emails: ${error.message}`
    };
  }
}

// Get emails from a specific sender
async function getEmailsFromSender(companyId, sender, maxResults = 5) {
  try {
    const emails = await findEmailsFromSender(companyId, sender, maxResults);
    
    if (!emails || emails.length === 0) {
      return {
        success: true,
        message: `You don't have any emails from ${sender} in your inbox.`,
        data: null
      };
    }
    
    return {
      success: true,
      message: `Here are your emails from ${sender}:`,
      data: {
        type: 'senderEmails',
        sender: sender,
        emails: emails
      }
    };
  } catch (error) {
    console.error(`Error fetching emails from ${sender}:`, error);
    return {
      success: false,
      message: `Error fetching emails from ${sender}: ${error.message}`
    };
  }
}

// Get emails with a specific subject
async function getEmailsBySubject(companyId, subject, maxResults = 5) {
  try {
    const emails = await findEmailsBySubject(companyId, subject, maxResults);
    
    if (!emails || emails.length === 0) {
      return {
        success: true,
        message: `You don't have any emails with the subject "${subject}" in your inbox.`,
        data: null
      };
    }
    
    return {
      success: true,
      message: `Here are your emails with the subject "${subject}":`,
      data: {
        type: 'subjectEmails',
        subject: subject,
        emails: emails
      }
    };
  } catch (error) {
    console.error(`Error fetching emails with subject ${subject}:`, error);
    return {
      success: false,
      message: `Error fetching emails with subject "${subject}": ${error.message}`
    };
  }
}

// Get emails related to a specific topic
async function getEmailsByTopic(companyId, topic, maxResults = 5) {
  try {
    const emails = await findEmailsByTopic(companyId, topic, maxResults);
    
    if (!emails || emails.length === 0) {
      return {
        success: true,
        message: `You don't have any emails related to "${topic}" in your inbox.`,
        data: null
      };
    }
    
    return {
      success: true,
      message: `Here are your emails related to "${topic}":`,
      data: {
        type: 'topicEmails',
        topic: topic,
        emails: emails
      }
    };
  } catch (error) {
    console.error(`Error fetching emails related to ${topic}:`, error);
    return {
      success: false,
      message: `Error fetching emails related to "${topic}": ${error.message}`
    };
  }
} 